import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ETaxDocTypeGroup } from "../../types/e-tax.type";
import { formatNumber, numberWithCommas } from "../../utils";
import { DOC_TYPE_CHIP_COLOR } from "./documentMeta";
import { AmountRow } from "./documentCells";

export interface SendSummary {
  documentCount: number;
  netTotal: number;
  amountBeforeVat: number;
  vatAmount: number;
  typeCounts: { INV: number; DN: number; CN: number };
}

const BILL_DOC_TYPES: readonly ETaxDocTypeGroup[] = ["INV", "DN", "CN"];

interface ConfirmSendModalProps {
  open: boolean;
  summary: SendSummary;
  loading: boolean;
  /** The document types whose counts are shown; the bill list's three by default. */
  documentTypes?: readonly ETaxDocTypeGroup[];
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ConfirmSendModal({
  open,
  summary,
  loading,
  documentTypes = BILL_DOC_TYPES,
  onCancel,
  onConfirm,
}: ConfirmSendModalProps) {
  return (
    // While a send (or the send-all preparation) is in flight the dialog must stay put: Esc,
    // a backdrop click or the X would hide a request that is still writing batches.
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      disableEscapeKeyDown={loading}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        ส่งเอกสาร E-Tax
        <IconButton
          aria-label="close"
          onClick={onCancel}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          ทั้งหมด {numberWithCommas(summary.documentCount)} รายการ
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            มูลค่ารวมสุทธิ (Net Total)
          </Typography>
          <Box display="flex" alignItems="baseline" gap={0.5}>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(summary.netTotal)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              บาท
            </Typography>
          </Box>
        </Box>

        <AmountRow label="มูลค่าก่อน VAT" value={`${formatNumber(summary.amountBeforeVat)} บาท`} />
        <AmountRow label="ภาษีมูลค่าเพิ่ม" value={`${formatNumber(summary.vatAmount)} บาท`} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            จำนวนเอกสารรวม
          </Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            {numberWithCommas(summary.documentCount)} ใบ
          </Typography>
        </Box>

        <Divider sx={{ mb: 1.5 }} />
        <Box display="flex" gap={1} flexWrap="wrap">
          {documentTypes.map((type) => {
            const style = DOC_TYPE_CHIP_COLOR[type];
            return (
              <Chip
                key={type}
                label={`${type}: ${summary.typeCounts[type]}`}
                size="small"
                sx={{ color: style.color, bgcolor: style.bg, fontWeight: 500 }}
              />
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={loading}>
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          loading={loading}
          disabled={summary.documentCount === 0}
        >
          ส่งทันที
        </Button>
      </DialogActions>
    </Dialog>
  );
}
